/**
 * MyTypePermissionsReadOnly — spec 2026-02-XX rev13 · point F
 * ------------------------------------------------------------
 * Section en lecture seule affichée dans « Mon profil » pour les
 * utilisateurs non-admin. Liste les types de documents qui leur sont
 * assignés avec un badge par action.
 *
 * Rendu :
 *   - Une carte par type assigné.
 *   - En-tête de carte : pastille couleur du type + nom.
 *   - Badges Voir / Créer / Modifier / Faire avancer — verts si
 *     l'action est autorisée, gris sinon.
 *   - Colonne « Clôturer » toujours verrouillée (icône 🔒), jamais
 *     activable pour un non-admin — verrou métier immuable.
 *   - Mention explicite en bas : la modification passe par un Admin.
 *
 * Endpoint : ``GET /api/user-type-permissions/me/effective``. Si le
 * backend renvoie ``is_admin: true`` (bypass total), la section n'est
 * pas affichée.
 */
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { Card } from "@/components/ui/card";
import { LockIcon as LockIcon, ShieldCheck, Info } from "lucide-react";
import api from "@/lib/api";

const ACTIONS = [
  { key: "view",    label: "Voir" },
  { key: "create",  label: "Créer" },
  { key: "edit",    label: "Modifier" },
  { key: "advance", label: "Faire avancer" },
];

function ActionBadge({ label, active }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={active
        ? { background: "rgba(34,197,94,0.14)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.35)" }
        : { background: "#232326", color: "#6B7280", border: "1px solid #2A2A2A" }}
    >
      {active && <ShieldCheck size={11} />}
      {label}
    </span>
  );
}

function CloseLockedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      title="Réservé aux administrateurs — verrou métier immuable"
      style={{
        background: "rgba(107,114,128,0.12)",
        color: "#8B92A0",
        border: "1px dashed rgba(107,114,128,0.35)",
      }}
    >
      <LockIcon size={11} /> Clôturer
    </span>
  );
}

export default function MyTypePermissionsReadOnly() {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [entries, setEntries] = useState([]);
  const [types, setTypes] = useState({}); // id → {name, color}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [effRes, typesRes] = await Promise.all([
          api.get("/user-type-permissions/me/effective"),
          api.get("/document-types"),
        ]);
        if (cancelled) return;
        setIsAdmin(!!effRes.data?.is_admin);
        setEntries(effRes.data?.entries || []);
        const byId = {};
        (typesRes.data || []).forEach((t) => { byId[t.id] = { name: t.name, color: t.color, is_deleted: t.is_deleted }; });
        setTypes(byId);
      } catch {
        toast.error("Impossible de charger vos droits par type");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Les admins bénéficient d'un accès total — pas de section à afficher.
  if (isAdmin) return null;
  if (loading) {
    return (
      <Card className="bg-card border-vertec-borderDark p-6" data-testid="my-type-perms-loading">
        <div className="text-sm text-muted-foreground italic">Chargement de vos droits…</div>
      </Card>
    );
  }

  const active = entries
    .map((e) => ({ ...e, type: types[e.type_id] }))
    .filter((e) => e.type && !e.type.is_deleted)
    .sort((a, b) => (a.type.name || "").localeCompare(b.type.name || ""));

  return (
    <Card className="bg-card border-vertec-borderDark p-6" data-testid="my-type-perms">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck size={18} className="text-[#B7A96A]" />
        <h3 className="text-base font-semibold">Droits par type de document</h3>
      </div>

      {active.length === 0 ? (
        <div
          className="text-sm text-muted-foreground p-4 rounded-md text-center"
          style={{ background: "rgba(107,114,128,0.06)", border: "1px dashed rgba(107,114,128,0.25)" }}
          data-testid="my-type-perms-empty"
        >
          Aucun type de document ne vous est encore assigné. Contactez un administrateur
          si vous pensez que c'est une erreur.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {active.map((e) => {
            const allowed = new Set(e.actions || []);
            return (
              <div
                key={e.type_id}
                className="rounded-md p-3"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`my-type-perms-card-${e.type_id}`}
              >
                {/* En-tête : pastille couleur + nom */}
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full shrink-0"
                    style={{ background: e.type.color || "#6B7280" }}
                  />
                  <div className="font-medium text-sm truncate">{e.type.name}</div>
                </div>
                {/* Badges d'actions autorisées */}
                <div className="flex flex-wrap gap-1.5">
                  {ACTIONS.map((a) => (
                    <ActionBadge key={a.key} label={a.label} active={allowed.has(a.key)} />
                  ))}
                  <CloseLockedBadge />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mention explicite : passage par un admin pour toute modification */}
      <div
        className="mt-4 flex items-start gap-2 text-xs p-3 rounded-md"
        style={{ background: "rgba(90,158,224,0.08)", border: "1px solid rgba(90,158,224,0.2)", color: "#B0C4DE" }}
      >
        <Info size={13} className="mt-0.5 shrink-0 text-[#5A9EE0]" />
        <span>
          Cette liste est en lecture seule. Toute modification (ajout, retrait, changement
          d'action) doit être demandée à un administrateur — Paramètres &gt; Utilisateurs &gt;
          Droits par type de document.
        </span>
      </div>
    </Card>
  );
}
